import React, { useEffect } from 'react';
import App from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import moment from 'moment';
import { registerLocale } from 'react-datepicker';
import dateFnsRuLocale from 'date-fns/locale/ru';
// @ts-ignore
import momentRuLocale from 'moment/locale/ru';
import smoothscroll from 'smoothscroll-polyfill';

import '@/assets/css/index.css';

import {
  cookie,
  isServer,
  useFixedVhProperty,
  useProgressBar,
} from '@tager/web-core';
import { AdminBar } from '@tager/web-panel';

import withRedux from '@/hocs/withRedux';
import withPerfLogs from '@/hocs/withPerfLogs';
import { CustomApp_Component } from '@/typings/hocs';
import { checkAuthorizationThunk } from '@/store/reducers/auth';
import { getMenuItemListThunk } from '@/store/reducers/tager/menus';
import { getSettingItemListThunk } from '@/store/reducers/tager/settings';
import {
  accessTokenCookieName,
  userApiService,
} from '@/services/users/users-service';
import { fetchDealersThunk } from '@/store/reducers/dealers';
import { StyledToastContainer } from '@/components/ToastContainer';
import { colors, getFont } from '@/constants/theme';
import { useSiteNavigationMicromarking } from '@/layout/hooks';
import AppProvider from '@/components/AppProvider';
import { useIsHideLayout } from '@/hooks/useIsHideLayout';
import RemoveCookieElement from '@/components/RemoveCookieElement';

moment.updateLocale('ru', momentRuLocale);
registerLocale('ru', dateFnsRuLocale);

/**
 * Custom App documentation
 * https://nextjs.org/docs/advanced-features/custom-app
 */
const CustomApp: CustomApp_Component = (props) => {
  const siteNavigationMicromarking = useSiteNavigationMicromarking();

  const isHideLayout = useIsHideLayout();

  useProgressBar({ showSpinner: false });

  useFixedVhProperty({ shouldListenResize: true });

  const { Component, pageProps } = props;

  // Workaround for https://github.com/zeit/next.js/issues/8592
  // @ts-ignore
  const { err } = props;
  const modifiedPageProps = { ...pageProps, err };

  useEffect(() => {
    smoothscroll.polyfill();
  }, []);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
      </Head>

      <AdminBar />
      <RemoveCookieElement />

      <style jsx global>
        {`
          * {
            font-family: ${getFont()};
          }
        `}
      </style>

      <AppProvider>
        <Component {...modifiedPageProps} />
      </AppProvider>

      <StyledToastContainer theme="colored" />

      <Script strategy="beforeInteractive" src="/scripts/lazysizes.min.js" />
      <Script
        strategy="beforeInteractive"
        noModule
        src="https://unpkg.com/core-js-bundle@3.6.5/index.js"
      />

      {!isHideLayout && (
        <>
          <script
          //  strategy="beforeInteractive"
          //  crossOrigin="anonymous"
            src={`${
              process.env.NEXT_PUBLIC_ENV === 'production'
                ? 'https://cookie.atlantm.by/script.js'
                : 'https://cookie.dev.atlantm.by/script.js'
            }`}
          />

          {process.env.NEXT_PUBLIC_GTM && (
            <Script
              id="atlantmCookie"
              strategy="beforeInteractive"
              dangerouslySetInnerHTML={{
                __html: `window.atlantmCookie = {
                    color: '${colors.main}',
                    policyUrl: '/cookie',
                    analyticsServices: ${
                      process.env.NEXT_PUBLIC_GTM
                        ? `{
                        gtm: ${
                          '[' +
                          process.env.NEXT_PUBLIC_GTM.split(',')
                            .map((item) => "'" + item + "'")
                            .join(',') +
                          ']'
                        },                   
                    }`
                        : ''
                    },
                  };
               `,
              }}
            />
          )}
        </>
      )}

      {process.env.NEXT_PUBLIC_GTM && process.env.NEXT_PUBLIC_VWO_ID && (
        <Script
          id="vwoCode"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: `window._vwo_code || (function() {
                     var account_id=${process.env.NEXT_PUBLIC_VWO_ID},
                     version=2.0,
                     settings_tolerance=2000,
                     hide_element='body',
                     hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
                     /* DO NOT EDIT BELOW THIS LINE */
                     f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(){if(!f){f=true;var e=d.getElementById('_vis_opt_path_hides');if(e)e.parentNode.removeChild(e)}},finished:function(){return f},load:function(e){var t=this.getSettings(),n=d.createElement('script'),i=this;if(t){n.textContent=t;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);i.load(e)}}else{n.fetchPriority='high';n.src=e;n.type='text/javascript';n.onerror=function(){w._vwo_code.finish()};d.getElementsByTagName('head')[0].appendChild(n)}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){w._vwo_code.finish();stT.removeItem(cK)},e);var t=d.currentScript,n=d.createElement('style'),i=this.hide_element(),r=t&&!t.async&&i?i+this.hide_element_style():'',c=d.getElementsByTagName('head')[0];n.setAttribute('id','_vis_opt_path_hides');v&&n.setAttribute('nonce',v.nonce);n.setAttribute('type','text/css');if(n.styleSheet)n.styleSheet.cssText=r;else n.appendChild(d.createTextNode(r));c.appendChild(n);this.load('https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version)}};w._vwo_code=code;code.init();})();(function(){var i=window;function t(){if(i._vwo_code){var e=t.hidingStyle=document.getElementById('_vis_opt_path_hides')||t.hidingStyle;if(!i._vwo_code.finished()&&!_vwo_code.libExecuted&&(!i.VWO||!VWO.dNR)){if(!document.getElementById('_vis_opt_path_hides')){document.getElementsByTagName('head')[0].appendChild(e)}requestAnimationFrame(t)}}}t()})();`,
          }}
        />
      )}
      {process.env.NEXT_PUBLIC_ENV === 'production' && (
        <Script
          id="roistat"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{
            __html: `(function(w, d, s, h, id) {
                     w.roistatProjectId = id; w.roistatHost = h;
                     var p = d.location.protocol == "https:" ? "https://" : "http://";
                     var u = /^.*roistat_visit=[^;]+(.*)?$/.test(d.cookie) ? "/dist/module.js" : "/api/site/1.0/"+id+"/init?referrer="+encodeURIComponent(d.location.href);
                     var js = d.createElement(s); js.charset="UTF-8"; js.async = 1; js.src = p+h+u; var js2 = d.getElementsByTagName(s)[0]; js2.parentNode.insertBefore(js, js2);
                     })(window, document, 'script', 'cloud.roistat.com', '91372a7e2a1f670825b5e1af9c68b521');`,
          }}
        />
      )}
      <Script
        id="siteNavigation"
        type="application/ld+json"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{
          __html: siteNavigationMicromarking,
        }}
      />
    </>
  );
};

CustomApp.getInitialProps = async (appContext) => {
  const { ctx } = appContext;

  try {
    if (isServer() && ctx.req) {
      userApiService.setAccessToken(cookie.get(accessTokenCookieName, ctx.req));

      const hrStart = process.hrtime();

      await Promise.all([
        ctx.store.dispatch(getMenuItemListThunk('header')),
        ctx.store.dispatch(getMenuItemListThunk('header_mobile')),
        ctx.store.dispatch(getMenuItemListThunk('footer')),
        ctx.store.dispatch(getSettingItemListThunk()),
        ctx.store.dispatch(fetchDealersThunk()),
        ctx.store.dispatch(checkAuthorizationThunk()),
      ]);

      const hrEnd = process.hrtime(hrStart);

      console.info(
        'Execution time (hr): %ds %dms',
        hrEnd[0],
        hrEnd[1] / 1000000
      );
    }
  } catch (error) {
    console.log('error = ', error);
  }

  /** calls page's `getInitialProps` and fills `appProps.pageProps` */
  const appProps = await App.getInitialProps(appContext);

  return { ...appProps };
};

export default withRedux(withPerfLogs(CustomApp));
